import { zodResolver } from '@hookform/resolvers/zod';
import {
  IListJobFetchParams,
  IListOrderFetchParams,
  IResourceFetchParams,
  MN_JOBS,
  MN_ORDERS,
  MN_RESOURCES,
} from '@jobcost-monorepo/crud';
import { ReactElement, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { FinancialReportSchema } from './validationSchema';
import { Button, Dropdown } from '@jobcost-monorepo/ui';
import { useTranslation } from '@jobcost-monorepo/i18n';
import { KeyValue } from '@jobcost-monorepo/utils';
import {
  getMonthsKeyValues,
  getYearsKeyValues,
} from '@jobcost-monorepo/crud/utils/helpers/helperKeyValues';
import DropdownCrud from '@jobcost-monorepo/crud/ui/DropdownCrud';
import { IFinancialReportFetchParams, ISearch } from '../types';
import dayjs from '@jobcost-monorepo/dayjs-config';

interface IProps {
  handleSearch: (
    startDate: string,
    endDate: string,
    resourceId?: number,
    orderId?: number,
    customerId?: number,
    jobId?: number,
  ) => void;
  params?: IFinancialReportFetchParams;
}

const FinancialReportdHeader = ({
  handleSearch,
  params,
}: IProps): ReactElement => {
  const { t } = useTranslation(['financial-report']);

  const defaultValues: ISearch = {
    startMonth: dayjs.utc(params?.startDate).month(),
    endMonth: dayjs.utc(params?.endDate).month(),
    year: dayjs.utc(params?.startDate).year(),
    resourceId: params?.resourceId,
    jobId: params?.jobId,
    customerId: params?.customerId,
    orderId: params?.orderId,
  };

  const months: KeyValue[] = getMonthsKeyValues();
  const years: KeyValue[] = getYearsKeyValues();

  const onSubmit: SubmitHandler<ISearch> = (data) => {
    const startDate = dayjs
      .utc(`${data.year}-${data.startMonth + 1}-01`)
      .startOf('month')
      .format('YYYY-MM-DD');
    const endDate = dayjs
      .utc(`${data.year}-${data.endMonth + 1}-01`)
      .endOf('month')
      .format('YYYY-MM-DD');
    const resourceId = data.resourceId;
    const jobId = data.jobId;
    const orderId = data.orderId;
    const customerId = data.customerId;

    handleSearch(startDate, endDate, resourceId, orderId, customerId, jobId);
  };

  const methods = useForm<ISearch>({
    mode: 'onSubmit',
    values: defaultValues,
    defaultValues: JSON.parse(JSON.stringify(defaultValues)),
    resolver: zodResolver(FinancialReportSchema),
  });

  const { handleSubmit, watch, setValue } = methods;
  const resourceId = watch('resourceId');
  const jobId = watch('jobId');

  useEffect(() => {
    if (!resourceId) {
      setValue('jobId', undefined);
    }
    if (!jobId) {
      setValue('orderId', undefined);
    }
  }, [resourceId, jobId]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <div className="flex overflow-y-auto max-h-32 flex-wrap gap-2 items-center">
          <Dropdown
            name="year"
            errorName="year"
            label={t('filter.year')}
            items={years}
            searchable
            width="w-24"
          />

          <Dropdown
            name="startMonth"
            errorName="startMonth"
            label={t('filter.startMonth')}
            items={months}
            searchable
            width="w-32"
          />

          <Dropdown
            name="endMonth"
            errorName="endMonth"
            label={t('filter.endMonth')}
            items={months}
            searchable
            width="w-32"
          />

          <DropdownCrud<ISearch, IResourceFetchParams>
            moduleName={MN_RESOURCES}
            linkedField={'resourceId'}
            linkedFieldForDefaultValue={'resource.name'}
            label={t('filter.resourceId')}
            width="w-56"
          />

          <DropdownCrud<ISearch, IListJobFetchParams>
            moduleName={MN_JOBS}
            linkedField={'jobId'}
            linkedFieldForDefaultValue={'job.name'}
            dependsOnFields={['resourceId']}
            dependsOnFieldsForSearch={['resourceId']}
            label={t('filter.jobId')}
            width="w-56"
          />

          <DropdownCrud<ISearch, IListOrderFetchParams>
            moduleName={MN_ORDERS}
            linkedField={'orderId'}
            linkedFieldForDefaultValue={'order.name'}
            dependsOnFields={['jobId', 'resourceId']}
            dependsOnFieldsForSearch={['jobId', 'resourceId']}
            label={t('filter.orderId')}
            width="w-56"
          />

          {/* <DropdownCrud<ISearch, IListCustomerFetchParams>
            moduleName={MN_CUSTOMERS}
            linkedField={'customerId'}
            linkedFieldForDefaultValue={'customer.name'}
            label={t('filter.customerId')}
            width="w-32"
          /> */}

          <Button type="submit">{t('filter.update')}</Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default FinancialReportdHeader;
